import React from 'react';
import { motion, HTMLMotionProps } from 'framer-motion';
import { useThemeColors } from '../hooks/useThemeColors';
import { cn } from '../utils/cn';

type ButtonVariant = 'primary' | 'secondary';
type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonProps extends Omit<HTMLMotionProps<'button'>, 'style'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  children: React.ReactNode;
  className?: string;
  icon?: React.ReactNode;
  'aria-label'?: string;
}

export default function Button({
  variant = 'primary',
  size = 'md',
  children,
  className,
  icon,
  ...props
}: ButtonProps) {
  const colors = useThemeColors();

  const sizeClasses = {
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-4 py-2',
    lg: 'px-6 py-3 text-lg',
  };

  const getButtonColors = () => {
    switch (variant) {
      case 'primary':
        return {
          backgroundColor: colors.button.primary.background,
          textColor: colors.button.primary.text,
          hover: colors.button.primary.backgroundHover,
        };
      case 'secondary':
        return {
          backgroundColor: colors.button.secondary.background,
          textColor: colors.button.secondary.text,
          hover: colors.button.secondary.backgroundHover,
        };
    }
  };

  const buttonColors = getButtonColors();

  return (
    <motion.button
      className={cn(
        'flex items-center gap-2 rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 transition-opacity hover:opacity-90',
        sizeClasses[size],
        className
      )}
      style={{
        backgroundColor: buttonColors.backgroundColor,
        color: buttonColors.textColor,
      }}
      whileHover={{
        scale: 1.05,
        backgroundColor: buttonColors.hover,
      }}
      whileTap={{ scale: 0.95 }}
      {...props}
    >
      {icon}
      {children}
    </motion.button>
  );
}
